import { CustomCSSProperties, StyleProp, TBreakpoints, ValidCSSValue } from '../types'
import { breakpointsArray } from 'src/styles/js/breakpoints'

// Helper function to convert a number to rem units
const convertToRem = (value: number) => `${value / 16}rem`

export const generateBreakpointStyles = <T extends ValidCSSValue>(
  property: StyleProp<T>,
  variableName: string,
  isUnitless: boolean = false,
): CustomCSSProperties => {
  const customStyles: CustomCSSProperties = {}

  if (typeof property === 'number') {
    // Convert numeric value to rem and apply it globally to all breakpoints
    const remValue = isUnitless ? property : convertToRem(property)
    breakpointsArray.forEach((bp) => {
      customStyles[`--${variableName}-${bp}`] = remValue
    })
  } else if (
    typeof property === 'string' ||
    (typeof property === 'number' && `${property}`.includes('%'))
  ) {
    // Handle string values like 'auto', 'inherit', 'initial', 'unset', and percentages
    breakpointsArray.forEach((bp) => {
      customStyles[`--${variableName}-${bp}`] = property
    })
  } else if (property && typeof property === 'object') {
    let lastValue: string | number | undefined = undefined

    // Forward pass to fill in values upwards
    breakpointsArray.forEach((bp) => {
      const breakpoint = bp as TBreakpoints
      let value = property[breakpoint]

      if (typeof value === 'number') {
        value = convertToRem(value) as T // Convert number to rem
      }

      if (value !== undefined) {
        lastValue = value
        customStyles[`--${variableName}-${breakpoint}`] = value
      } else if (lastValue !== undefined) {
        customStyles[`--${variableName}-${breakpoint}`] = lastValue
      }
    })

    // Reverse pass to fill in values downwards
    lastValue = undefined // Reset lastValue for downward pass
    for (let i = breakpointsArray.length - 1; i >= 0; i--) {
      const breakpoint = breakpointsArray[i] as TBreakpoints
      let value = property[breakpoint]

      if (typeof value === 'number') {
        value = convertToRem(value) as T // Convert number to rem
      }

      if (value !== undefined) {
        lastValue = value
        customStyles[`--${variableName}-${breakpoint}`] = lastValue
      } else if (lastValue !== undefined) {
        customStyles[`--${variableName}-${breakpoint}`] = lastValue
      }
    }
  }

  return customStyles
}

export default generateBreakpointStyles
